import { Controller } from '@hotwired/stimulus'
import EditorJS from '@editorjs/editorjs'
import Image from '@editorjs/image'
import Underline from '@editorjs/underline'
import DragDrop from 'editorjs-drag-drop'
import CustomHeader from '../../lib/editorjs-plugins/custom-header'
import CustomList from '../../lib/editorjs-plugins/custom-list'
import LinkButton from '../../lib/editorjs-plugins/link-button'
import RecommendedArticles from '../../lib/editorjs-plugins/recommend-articles'

export default class extends Controller {
  static targets = ['form', 'leadTextEditor', 'leadTextHidden', 'contentEditor', 'contentHidden']
  static i18nData = {
    messages: {
      ui: {
        'blockTunes': {
          'toggler': {
            'Click to tune': 'クリックで調整',
            'or drag to move': 'またはドラッグで移動',
          },
        },
        'inlineToolbar': {
          'converter': {
            'Convert to': '変換',
          },
        },
        'toolbar': {
          'toolbox': {
            'Add': '追加',
          },
        },
        'popover': {
          'Filter': 'フィルター',
          'Nothing found': '該当なし',
          'Convert to': '変換',
        },
      },
      toolNames: {
        'Text': 'テキスト',
        'Heading': '見出し',
        'Unordered List': 'リスト',
        'Ordered List': '番号リスト',
        'Link': 'リンク',
        'Bold': '太字',
        'Italic': '斜体',
        'Underline': '下線',
        'Image': '画像',
      },
      tools: {
        'link': {
          'Add a link': 'リンクを追加',
        },
        'header': {
          'Heading 2': '中見出し',
          'Heading 3': '小見出し',
        },
        'list': {
          'Unordered': 'リスト',
          'Ordered': '番号リスト',
          'Start with': '開始番号',
        },
        'image': {
          'Caption': 'キャプション',
          'Select an Image': '画像を選択',
        },
        'convertTo': {
          'Convert to': '変換',
        },
      },
      blockTunes: {
        'delete': {
          'Delete': '削除',
          'Click to delete': 'クリックで削除',
        },
        'moveUp': {
          'Move up': '上へ移動',
        },
        'moveDown': {
          'Move down': '下へ移動',
        },
      },
    },
  }

  connect() {
    this.leadTextEditor = new EditorJS({
      holder: this.leadTextEditorTarget,
      tools: {
        list: {
          class: CustomList,
          inlineToolbar: true,
          config: {
            counterTypes: ['numeric']
          },
          toolbox: [
            {
              data: { style: 'unordered' },
            },
            {
              data: { style: 'ordered' },
            },
          ],
        },
        underline: Underline,
      },
      onReady: () => {
        new DragDrop(this.leadTextEditor);
      },
      data: this.leadTextHiddenTarget.value ? JSON.parse(this.leadTextHiddenTarget.value) : {},
      i18n: this.i18nData,
      minHeight: 80,
    })

    this.contentEditor = new EditorJS({
      holder: this.contentEditorTarget,
      tools: {
        header: {
          class: CustomHeader,
          config: {
            levels: [2, 3],
            defaultLevel: 2,
          },
        },
        list: {
          class: CustomList,
          inlineToolbar: true,
          config: {
            counterTypes: ['numeric']
          },
          toolbox: [
            {
              data: { style: 'unordered' },
            },
            {
              data: { style: 'ordered' },
            },
          ],
        },
        image: {
          class: Image,
          config: {
            endpoints: {
              byFile: '/admin/articles/upload_image',
            },
            additionalRequestHeaders: {
              'X-CSRF-Token': this.csrfToken(),
            },
            features: {
              background: false,
              border: false,
              caption: false,
              stretch: false,
            },
          },
        },
        underline: Underline,
        linkButton: LinkButton,
        recommendedArticles: RecommendedArticles,
      },
      onReady: () => {
        new DragDrop(this.contentEditor);
      },
      data: this.contentHiddenTarget.value ? JSON.parse(this.contentHiddenTarget.value) : {},
      i18n: this.i18nData,
      minHeight: 80,
    })

    this.formSubmitEventListener = this.saveAndSubmit.bind(this)
    this.formTarget.addEventListener('submit', this.formSubmitEventListener)
  }

  disconnect() {
    this.formTarget.removeEventListener('submit', this.formSubmitEventListener)
    this.formSubmitEventListener = null
    this.contentEditor.destroy()
    this.contentEditor = null
  }

  async saveAndSubmit(event) {
    event.preventDefault()

    const [leadTextData, contentData] = await Promise.all([
      this.leadTextEditor.save(),
      this.contentEditor.save()
    ])
    this.leadTextHiddenTarget.value = JSON.stringify(leadTextData)
    this.contentHiddenTarget.value = JSON.stringify(contentData)

    this.formTarget.submit()
  }

  csrfToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    return metaTag ? metaTag.content : '';
  }
}
